import { useProjectData } from "@/contexts/ProjectData"
import { useSuppressProjectMap } from "@/contexts/ProjectMapVisibility"
import { useLocalGeospatialData } from "@/hooks/useLocalGeospatialData"
import { useGeospatialUpload } from "@/hooks/useGeospatialUpload"
import { GeospatialLocationSelector } from "@/components/geospatial/GeospatialLocationSelector"
import { SeedConflictPrompt } from "@/components/geospatial/SeedConflictPrompt"
import { FormExitGuard } from "@/components/common/FormExitGuard"
import { PhotosForm } from "@/components/photos/PhotosForm"
import {
  PhotoChangesProvider,
  usePhotoChanges,
} from "@/components/photos/PhotosFormProvider"
import {
  useDraftRestore,
  useDraftRestoredNotice,
  useFormDraft,
} from "@/hooks/useFormDraft"
import { useHasUnsavedChanges } from "@/hooks/useHasUnsavedChanges"
import { draftPhotoToStaged } from "@/lib/drafts/draftPhotos"
import { deleteDraft } from "@/lib/drafts/draftsDb"
import { saveDraftForExit } from "@/lib/drafts/saveDraftForExit"
import { buildResumeKey } from "@/lib/drafts/types"
import { stagedPhotosToInputs } from "@/hooks/usePhotoMutations"
import { persistStagedPhotosForNewEntity } from "@/hooks/persistStagedPhotosForNewEntity"
import { toast } from "@fieldbook/ui/toaster"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useCallback, useMemo, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema"
import { Button } from "@fieldbook/ui/button"
import { Input } from "@fieldbook/ui/input"
import { Label } from "@fieldbook/ui/label"
import { Spinner } from "@fieldbook/ui/spinner"
import { Textarea } from "@fieldbook/ui/textarea"
import { ColorPicker } from "@fieldbook/ui/color-picker"
import { DEFAULT_GEOSPATIAL_COLOR } from "@fieldbook/common/utils"
import { MapPin, Shapes } from "lucide-react"
import { useAuth } from "@/hooks/useAuth"
import z from "zod"
import {
  buildFormDefaults,
  geospatialFormSchema,
  GEOMETRY_TYPE_LABELS,
  hasSeedConflict,
  LOCATION_GEOMETRY_TYPES,
  seedFromWkt,
  type GeospatialDraft,
  type GeospatialFormValues,
  type GeospatialSeed,
  type LocationGeometryType,
} from "./-geospatialForm"

const searchParamSchema = z.object({
  wktGeometry: z.string().optional(),
  draftId: z.string().uuid().optional(),
})

export const Route = createFileRoute("/_private/projects/$id/geospatial/new")({
  component: NewGeospatialRecordRoute,
  validateSearch: searchParamSchema.parse,
})

/**
 * Resolves any autosaved draft before the form mounts. react-hook-form freezes
 * its defaults at mount, so this has to settle first — hence the separate
 * component rather than a branch inside NewGeospatialRecord.
 */
function NewGeospatialRecordRoute() {
  const { id: projectId } = Route.useParams()
  const { wktGeometry, draftId } = Route.useSearch()
  const seed = useMemo(() => seedFromWkt(wktGeometry), [wktGeometry])
  const { user } = useAuth()

  const resumeKey = buildResumeKey({
    kind: "geospatial",
    projectId,
    mode: "new",
  })

  const restore = useDraftRestore<GeospatialFormValues, null>(
    resumeKey,
    user?.id ?? null,
    draftId,
  )

  // Null until the user answers the conflict dialog below. Only consulted when
  // there is a conflict to answer.
  const [seedChoice, setSeedChoice] = useState<"resume" | "fresh" | null>(null)

  const startFresh = useCallback(() => {
    const abandonedId = restore.draft?.id
    setSeedChoice("fresh")
    // Not awaited: the new form autosaves under its own record id, so the two
    // writes can't collide, and holding the form closed on an IndexedDB delete
    // would be a worse trade than the delete landing a moment late.
    if (abandonedId) void deleteDraft(abandonedId)
  }, [restore.draft])

  if (restore.status === "pending") {
    return (
      <div className="bg-background absolute inset-0 z-20 flex items-center justify-center">
        <Spinner />
      </div>
    )
  }

  const conflict = hasSeedConflict(seed, restore.draft)

  if (conflict && seedChoice === null) {
    return (
      <SeedConflictPrompt
        label={restore.draft?.label}
        onResume={() => setSeedChoice("resume")}
        onStartFresh={startFresh}
      />
    )
  }

  const draft = conflict && seedChoice === "fresh" ? null : restore.draft

  return (
    <PhotoChangesProvider
      initialPhotos={[]}
      initialStagedPhotos={draft?.photos.map(draftPhotoToStaged)}
    >
      <NewGeospatialRecord
        resumeKey={resumeKey}
        restoredDraft={draft}
        seed={seed}
      />
    </PhotoChangesProvider>
  )
}

function NewGeospatialRecord({
  resumeKey,
  restoredDraft,
  seed,
}: {
  resumeKey: string
  restoredDraft: GeospatialDraft | null
  seed?: GeospatialSeed
}) {
  // This route covers the project map for its whole life. Releasing the map's
  // WebGL context and tile buffers here keeps the process small enough to
  // survive being backgrounded behind the camera.
  useSuppressProjectMap()

  const { id: projectId } = Route.useParams()
  const { draftId } = Route.useSearch()
  const navigate = useNavigate()
  const {
    data: { project },
  } = useProjectData()
  const { user, org } = useAuth()
  const { addRecord } = useLocalGeospatialData(projectId)
  const { mutateAsync: uploadRecord } = useGeospatialUpload()
  // Adopting a resumed draft's id continues that record rather than starting a
  // second one beside it, and keeps the uploads its staged photos were already
  // recorded against pointing at the right row.
  const recordIdRef = useRef(restoredDraft?.id ?? crypto.randomUUID())
  const [photoChanges] = usePhotoChanges()

  const form = useForm<GeospatialFormValues>({
    defaultValues: buildFormDefaults({ seed, draft: restoredDraft }),
    resolver: standardSchemaResolver(geospatialFormSchema),
    mode: "all",
  })

  const {
    handleSubmit,
    register,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = form

  const draft = useFormDraft({
    resumeKey,
    kind: "geospatial",
    projectId,
    ownerId: user?.id ?? null,
    entityId: recordIdRef.current,
    label: watch("name") || "New geospatial record",
    form,
    extras: null,
    photos: photoChanges.add,
    restoredDraft,
    // Autosaving over a form that is already committing would race the discard
    // that follows a successful save.
    enabled: !isSubmitting,
  })

  useDraftRestoredNotice(restoredDraft, "geospatial record")

  const [showLocationSelector, setShowLocationSelector] = useState(false)
  const [notesFocus, setNotesFocus] = useState(false)
  // Which save is in flight, so the button the user pressed is the one that
  // reports back. react-hook-form's isSubmitting can't tell them apart.
  const [pendingSave, setPendingSave] = useState<"device" | "upload" | null>(
    null,
  )

  const geometryType = watch("geometry_type")
  const geometry = watch("geojson")
  const color = watch("color")

  const handleGeometryTypeChange = useCallback(
    (type: LocationGeometryType) => {
      setValue("geometry_type", type, { shouldDirty: true })
      // A geometry drawn as one shape isn't a valid value for another.
      setValue("geojson", undefined, {
        shouldDirty: true,
        shouldValidate: true,
      })
    },
    [setValue],
  )

  /**
   * Writes the record to local SQLite, then persists its photos.
   *
   * `upload` promotes the record out of the local-only table into the synced
   * one, which is what puts it in front of the rest of the project. It runs
   * last so the photo associations it carries across are already written —
   * they are staged into the local-only mirror alongside the record.
   */
  const saveRecord = useCallback(
    async (data: GeospatialFormValues, upload: boolean) => {
      clearErrors("root")

      // Guaranteed by the schema; narrows the optional away for addRecord.
      if (!data.geojson) return

      setPendingSave(upload ? "upload" : "device")

      try {
        if (!user) throw new Error("Not logged in")
        if (!org) throw new Error("No organisation selected")

        const now = new Date().toISOString()
        await addRecord({
          id: recordIdRef.current,
          project_id: projectId,
          name: data.name,
          notes: data.notes.trim() || null,
          geometry_type: data.geometry_type,
          geojson: data.geojson,
          properties: null,
          color: data.color,
          displayOnMap: true,
          uploaded: false,
          created_at: now,
          updated_at: now,
          created_by: user.id,
        })

        // After the record, so the association rows have a row to point at.
        // Sequential and awaited: these writes carry the photo bytes, and the
        // record is the only thing holding them once the form unmounts.
        const failedPhotos = await persistStagedPhotosForNewEntity({
          photos: stagedPhotosToInputs(photoChanges.add, org.id),
          entityId: recordIdRef.current,
          entityType: "geospatial_data",
          onError: (photo, error) => {
            console.error(
              `[Geospatial] Failed to save photo ${photo.id}:`,
              error,
            )
            toast.error(`Couldn't save ${photo.filename}.`)
          },
        })

        if (failedPhotos.length > 0) {
          // Stay on the form, and do not navigate.
          //
          // A photo that failed to persist exists only as the staged File this
          // form is holding. Navigating unmounts the form and drops it, so
          // leaving the screen here would destroy the last copy of the bytes
          // — and no amount of draft-keeping brings back a File the form was
          // no longer holding when it went away. Keeping the form mounted is
          // what makes "try again" mean anything.
          //
          // The record also stays local, unpromoted. Promoting it while a
          // draft carrying the same id survives puts the row in
          // geospatial_data while a retry writes the same id back into
          // local_geospatial_data: the record then shows twice through the
          // list's UNION, its new associations are stranded in the local-only
          // mirror, and a second "Save and upload" collides on the primary key
          // it already inserted. Retrying from a purely local record has none
          // of those problems.
          //
          // Every staged photo stays in the grid, including the ones that
          // already succeeded; pruning them would make them vanish from the
          // form the user is looking at. The retry skips rewriting those and
          // removes any the user unstaged in the meantime.
          toast.warning(
            [
              failedPhotos.length === 1
                ? "A photo couldn't be saved."
                : `${failedPhotos.length} photos couldn't be saved.`,
              upload
                ? "The record wasn't uploaded. Your work is still here — try saving again."
                : "Your work is still here — try saving again.",
            ].join(" "),
          )
          setPendingSave(null)
          return
        }

        if (upload) await uploadRecord(recordIdRef.current)

        // The record and every photo are in local storage now, so the draft
        // has nothing left to protect.
        await draft.discard()

        navigate({
          to: "/projects/$id/data/geospatial/$recordId",
          params: { id: projectId, recordId: recordIdRef.current },
        })
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Something went wrong while saving the record"
        setError("root", { message })
        setPendingSave(null)
      }
    },
    [
      user,
      org,
      addRecord,
      uploadRecord,
      projectId,
      navigate,
      photoChanges.add,
      draft,
      setError,
      clearErrors,
    ],
  )

  const handleSaveOnDevice = handleSubmit((data) => saveRecord(data, false))
  const handleSaveAndUpload = handleSubmit((data) => saveRecord(data, true))

  const hasUnsavedChanges = useHasUnsavedChanges({ form })

  // Leaving only: FormExitGuard discards the draft first.
  const handleCancel = useCallback(() => {
    navigate({ to: "/projects/$id", params: { id: projectId } })
  }, [navigate, projectId])

  // Nothing here blocks an explicit save the way a required species or
  // collection picker does elsewhere — a geospatial draft is savable with
  // whatever the user has entered so far.
  const handleSaveDraft = useCallback(
    () =>
      saveDraftForExit({
        validateIdentifier: () => true,
        save: draft.saveForExit,
      }),
    [draft],
  )

  if (!project) return null

  const geometryLabel =
    geometry !== undefined
      ? `${geometryType} location selected - tap to edit`
      : `Select ${geometryType} location`

  return (
    <div className="bg-background absolute inset-0 z-20 flex flex-col justify-between gap-2 pb-2">
      <div className="space-y-2 overflow-y-scroll">
        <div className="flex items-center p-2 text-2xl">
          <Shapes className="mr-2 size-6" />
          New Geospatial Record
        </div>

        <div className="flex flex-col gap-4 px-1">
          {/* Name */}
          <div className="space-y-1">
            <Label htmlFor="name">Name *</Label>
            <Input
              {...register("name")}
              id="name"
              className="h-12 text-lg"
              autoComplete="off"
              placeholder="Enter a name"
            />
            {errors.name && (
              <div className="mt-1 text-sm text-red-600">
                {errors.name.message}
              </div>
            )}
          </div>

          {/* Geometry Type */}
          <div className="space-y-1">
            <Label>Geometry Type</Label>
            <div className="flex gap-2">
              {LOCATION_GEOMETRY_TYPES.map((value) => (
                <Button
                  key={value}
                  type="button"
                  variant={geometryType === value ? "default" : "outline"}
                  onClick={() => handleGeometryTypeChange(value)}
                  className="flex-1"
                  size="sm"
                >
                  {GEOMETRY_TYPE_LABELS[value]}
                </Button>
              ))}
            </div>
          </div>

          {/* Location */}
          <div className="space-y-1">
            <Label>Location</Label>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowLocationSelector(true)}
              className="h-12 w-full justify-start text-lg"
            >
              <MapPin className="mr-2 size-5" />
              {geometryLabel}
            </Button>
            {errors.geojson && (
              <div className="mt-1 text-sm text-red-600">
                {errors.geojson.message}
              </div>
            )}
          </div>

          {/* Colour */}
          <div className="space-y-1">
            <Label>Colour</Label>
            <div className="flex items-center gap-2">
              <ColorPicker
                value={color}
                fallbackColor={DEFAULT_GEOSPATIAL_COLOR}
                label="Colour for new geospatial record"
                onChange={(value) =>
                  setValue("color", value, { shouldDirty: true })
                }
              />
              {color && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setValue("color", null, { shouldDirty: true })}
                >
                  Reset
                </Button>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              {...register("notes")}
              id="notes"
              className={`h-20 text-lg transition-all duration-500 ease-in-out ${
                notesFocus ? "h-40" : ""
              }`}
              placeholder="Enter notes"
              onFocus={() => setNotesFocus(true)}
              onBlur={() => setNotesFocus(false)}
            />
          </div>

          {/* Photos */}
          <PhotosForm
            entityType="geospatial_data"
            onCaptureStateChange={draft.setCaptureInFlight}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2 border-t border-green-800 px-1 pt-2">
        {errors.root?.message && (
          <div className="rounded-md border border-orange-300 bg-orange-50 p-3 text-sm text-orange-950">
            {errors.root.message}
          </div>
        )}
        <p className="text-muted-foreground text-xs">
          Records saved on this device stay here until you upload them to the
          project.
        </p>
        <FormExitGuard
          hasUnsavedChanges={hasUnsavedChanges}
          discardDraft={draft.discard}
          onCancel={handleCancel}
          onSaveDraft={handleSaveDraft}
          isEditingDraft={Boolean(draftId && restoredDraft?.savedByUser)}
        />
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={isSubmitting}
            className="h-12 flex-1 text-base"
            onClick={handleSaveOnDevice}
          >
            {pendingSave === "device" ? "Saving..." : "Save on device"}
          </Button>
          <Button
            type="button"
            disabled={isSubmitting}
            className="h-12 flex-1 text-base"
            onClick={handleSaveAndUpload}
          >
            {pendingSave === "upload" ? "Uploading..." : "Save and upload"}
          </Button>
        </div>
      </div>

      {/* Location Selector Overlay */}
      {showLocationSelector && project && (
        <GeospatialLocationSelector
          isOpen={showLocationSelector}
          onClose={() => setShowLocationSelector(false)}
          locationType={geometryType}
          project={project}
          value={geometry}
          onChange={(g) =>
            setValue("geojson", g, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            })
          }
        />
      )}
    </div>
  )
}
